import {useSearchTranslation} from "../../search/SearchContext";
import {useIsAdminOfAtleastOneMuseum} from "../../auths/useIsAdminOfAtleastOneMuseum";
import {useIsAppAdmin} from "../../auths/useIsAppAdmin";
import {useCallback, useEffect, useMemo, useState} from "react";
import {RestrictedAccessDialog} from "./RestrictedAccessDialog";
import {useAuthsState} from "../../auths/authsContext";

/**
 * Component showing a dialog to users who do not have access to any museum, or
 * have been granted access to one or more museums via a shared folder.
 *
 * If the user has not been granted access to any museum, nor has been granted
 * access via a shared folder, the component attempts to load the list of shared
 * folders. If the list is empty, the component shows a dialog with information
 * about how to gain access to the DAMS application. If the list contains one or
 * more folders, the component shows a dialog with a list of the folders and
 * allows the user to navigate to the contents of a selected folder.
 *
 * In any case, the component is only shown if the user is authenticated, but
 * does not have access to any museum.
 */
export const RestrictedAccessInformation = () => {

    const t = useSearchTranslation();

    const isAdminOfAtLeastOneMuseum = useIsAdminOfAtleastOneMuseum();

    const isAppAdmin = useIsAppAdmin();

    const isEkulturAdmin = useIsAppAdmin(window._env_.REACT_APP_EKULTUR_ID);

    const {userData} = useAuthsState();

    const [data, setData] = useState({
        accessLevelDetails: undefined
    });

    const hasUserOrEditorAccess = useCallback(() => {
        const {museums} = userData.appAccess;
        return Boolean(
            museums.find(m =>
                m.applications.find(a => a.id === window._env_.REACT_APP_ID
                    && ['User', 'Editor'].includes(a.role.name)))
        );
    }, [userData]);

    const getAdminUrl = useCallback(() => {
        const mode = window._env_.REACT_APP_X_QUOTE_MODE;
        if (mode === 'local' || mode === 'dev') {
            return 'https://beta-admin.ekultur.org/';
        }
        return 'https://admin.ekultur.org/';
    }, []);

    const accessLevels = useMemo(() => ([{
        level: 1,
        title: t('restrictedLevelOneTitle', 'Du har ikke tilgang til DAMS'),
        message: t('restrictedLevelOneMessage', 'Kontakt din nærmeste leder eller eKultur-administrator dersom du ønsker brukertilgang til applikasjonen.'),
        messageDetails: t('restrictedLevelOneMessageDetails', 'DAMS er en del av eKultur, et digitalt økosystem med felles infrastruktur for forvaltning og formidling i museumssektoren.'),
        linkText: t('restrictedLevelOneLinkText', 'Les mer om eKultur'),
        linkUrl: 'https://kulturit.org/ekultur'
    }, {
        level: 3,
        title: t('restrictedLevelThreeTitle', 'Du har ikke tilgang til DAMS'),
        message: t('restrictedLevelThreeMessage', 'Som eKultur-administrator har du mulighet til å redigere brukertilganger i'),
        linkText: t('restrictedLevelThreeLinkText', 'admin-applikasjonen'),
        linkUrl: getAdminUrl()
    }, {
        level: 4,
        title: t('restrictedLevelFourTitle', 'Du har administrator-tilgang i DAMS, men mangler tilgang til museets filer'),
        message: t('restrictedLevelFourMessage', 'Enkelte funksjoner er tilgjengelige i menyen, men for å se innhold, må din brukerkonto redigeres i'),
        linkText: t('restrictedLevelFourLinkText', 'admin-applikasjonen'),
        linkUrl: getAdminUrl()
    }]), [t, getAdminUrl]);

    /**
     * Finds the access level details for a given level.
     * @param {number} accessLevel The access level to find details for.
     * @returns {object} The access level details if found, undefined otherwise.
     */
    const getAccessLevelDetails = useCallback((accessLevel) => {
        return accessLevels.find(l => l.level === accessLevel);
    }, [accessLevels]);


    /**
     * Determines the access level for the user, based on their role and if they
     * have access to any museums.
     *
     * The access level is determined as follows:
     * - If the user is not an admin of any museum, and not an eKultur admin, the
     *   level is 1.
     * - If the user is an eKultur admin, but not an admin of any museum, the
     *   level is 3.
     * - If the user is an admin of at least one museum, but not an eKultur admin,
     *   the level is 4.
     *
     * The access level is used to determine the text and links displayed in the
     * welcome dialog.
     */
    const determineAccessLevel = useCallback(() => {
        let level = 0;

        if (!hasUserOrEditorAccess()) {
            if (!isAppAdmin && !isAdminOfAtLeastOneMuseum && !isEkulturAdmin) {
                level = 1;
            } else if (!isAppAdmin && !isAdminOfAtLeastOneMuseum && isEkulturAdmin) {
                level = 3;
            } else if (isAppAdmin && !isAdminOfAtLeastOneMuseum && !isEkulturAdmin) {
                level = 4
            }
        }

        if (level === 0) {
            return;
        }

        const details = getAccessLevelDetails(level);
        setData(prev => ({
            ...prev,
            accessLevelDetails: details
        }));
    }, [isAppAdmin, isAdminOfAtLeastOneMuseum, isEkulturAdmin, getAccessLevelDetails, hasUserOrEditorAccess]);

    /**
     * Hook used to determine whether folders have been shared or not.
     */
    useEffect(() => {
        if (userData) {
            determineAccessLevel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, determineAccessLevel]);

    const {accessLevelDetails} = data;

    return <>
        {
            typeof (accessLevelDetails) !== 'undefined'
            && <RestrictedAccessDialog show={true} config={accessLevelDetails}/>
        }
    </>;
};
